import { env } from '@/constants';
import { ArcualViewingRoomStatus } from '@/model/activation/viewing-rooms/ViewingRoomFilters';
import { activationRoutePaths, getActivationRoute } from '@/views/activation/activation-route-paths';
import { dashboardMessages } from '@/views/dashboard/dashboard.messages';
import {
  ActivationViewType,
  getActivationService,
  type Activation,
  type ActivationResponse,
} from '@arcual/lib-app-platform';

export async function transformViewingRooms({
  items,
  accountId,
  accountName,
}: {
  items: ActivationResponse[];
  accountId: string | null;
  accountName: string;
}) {
  return Promise.all(
    items.map(async (item) => {
      const childStats = await getActivationService().getActivationStats(item.id);
      return {
        id: item.id,
        name: item.name,
        subtitle: item.operatorId === accountId ? accountName : '',
        viewingRoomURL: `/viewing-rooms/${item.id}`,
        publishStatus: item.isPublished ? dashboardMessages.published : dashboardMessages.unpublished,
        status:
          item?.childMetadata?.visibility === ArcualViewingRoomStatus.PUBLIC
            ? dashboardMessages.public
            : dashboardMessages.private,
        listingsCount: childStats.listings.total,
        href: getActivationRoute(activationRoutePaths.ID, item.id),
      };
    }),
  );
}

export async function buildStorefrontSummary({
  activation,
  accountId,
  accountName,
  isLightMode,
  viewingRoomPageSize,
  viewingRoomPageMap,
}: {
  activation: ActivationResponse;
  accountId: string | null;
  accountName: string;
  isLightMode: boolean;
  viewingRoomPageSize: number;
  viewingRoomPageMap: Record<string, number>;
}) {
  const stats = await getActivationService().getActivationStats(activation.id);
  const viewingRooms = await getActivationService().getActivationViewingRooms(activation.id, {
    page: 1,
    pageSize: viewingRoomPageSize,
  });

  viewingRoomPageMap[activation.id] = 1;

  const childrenWithStats = await transformViewingRooms({
    items: viewingRooms.items,
    accountId,
    accountName,
  });

  return {
    id: activation.id,
    name: activation.name,
    subtitle: `${activation.subdomain}.${activation.domain}`,
    logo: (isLightMode ? activation.logoLightUrl : activation.logoDarkUrl) ?? '',
    listingsCount: stats.listings.total,
    children: childrenWithStats,
    viewingRooms: viewingRooms.total,
    publishStatus: activation.isPublished ? dashboardMessages.published : dashboardMessages.unpublished,
    status: dashboardMessages.publicStorefront,
    operatorId: activation.operatorId,
    canCreateNewViewingRoom: true,
    href: getActivationRoute(activationRoutePaths.ID, activation.id),
  };
}

export const isActivationViewingRoom = (activation?: ActivationResponse | Activation | null): boolean => {
  return Boolean(activation?.childMetadata) || activation?.viewType === ActivationViewType.VIEWING_ROOM;
};

export const buildActivationURL = (activation?: ActivationResponse | Activation | null): string => {
  if (!activation) return '';

  const domain = env.VITE_STOREFRONT_URL === 'localhost:8086' ? env.VITE_STOREFRONT_URL : activation.domain;

  if (isActivationViewingRoom(activation) && activation.childMetadata?.parentActivationSubdomain) {
    return `https://${activation.childMetadata?.parentActivationSubdomain}.${domain}/${activation?.subdomain}`;
  }
  return `https://${activation.subdomain}.${domain}`;
};

export const ABActivationsIds = {
  production: [
    '08083d27-958d-447b-8fc0-6cd92d6747ce', // Art Basel Paris
    'bc44eaa4-8ecc-4bb1-8dcc-2225e2bbc16c', // Art Basel Miami Beach 24 Art Discovery Experience
    '062845f1-1740-46d9-95fb-995c15fbf7ae', // Art Basel Hong Kong 25 Art Discovery Experience
    'ba0ffa27-4449-4cb2-b105-2951c75f68bf', // Art Basel x Microsoft
    'd584e0f5-dbc0-40f3-85fc-61f9c276ac23', // Art Basel in Basel 2025 Art Discovery Experience
    '70d968f9-83c0-4641-bbcc-c44575eb532d', // Art Basel Paris 2025 Art Discovery Experience
    '354e4221-9c73-4e45-bfa3-6775c8451f23', // Art Basel Miami Beach 2025 Art Discovery
  ],
  staging: [
    '9c23dd26-e690-4f16-8bf6-fafa932eca63', // Paris
    'd7c484df-cb80-4cde-af15-6fd48b5121eb', // Miami Beach
    'afba8554-a4c3-460d-8b7f-5f40b57f6f2a', // Qatar
    '1a214609-1a43-45d2-9101-6b7ce32d53ca', // Hong Kong
  ],
  development: [
    'e27c9afe-b6f7-4e8f-885b-1dab9cbf6c08', // matviy-test
  ],
  test: [
    'e27c9afe-b6f7-4e8f-885b-1dab9cbf6c08', // matviy-test
  ],
}

export const environmentNameForABChecks: string = env.VITE_ENV || 'test';

export const isAnABActivation = (activation?: ActivationResponse | Activation | null): boolean => {
  const validIds = ABActivationsIds[environmentNameForABChecks as keyof typeof ABActivationsIds || ''] || [];

  return (
    validIds.includes(activation?.id ?? '') || validIds.includes(activation?.childMetadata?.parentActivationId ?? '')
  );
}
